import { defaultTheme as theme } from '@automata/mui-theme';
import { InfoPopover, NumberField } from '@automata/ui';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import { Stack, SxProps } from '@mui/system';
import { useState } from 'react';

import { setWorkflowIsSaved } from '../../../../features/ui/uiSlice';
import { selectLabwareOffsetDefinitions } from '../../../../features/workflow/selectors';
import {
  removeLabwareOffsetDefinition,
  updateLabwareOffsetDefinitions,
} from '../../../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { DefinitionOrientation } from '../../../../zodSchemas/labwareOffsetDefinition';
import { UnitOfMeasurementSelect } from '../TaskFields/UnitOfMeasurementSelect';

const sx: SxProps = {
  width: '100%',
  flex: 1,
};

interface GrippingDimensionsProps {
  labwareDefinitionURI: string;
}

export const GrippingDimensions = ({
  labwareDefinitionURI,
}: GrippingDimensionsProps) => {
  const dispatch = useAppDispatch();
  const labwareOffsetDefinition = useAppSelector(
    selectLabwareOffsetDefinitions
  )[labwareDefinitionURI];

  const [orientation, setOrientation] = useState(
    DefinitionOrientation.LANDSCAPE
  );

  const handleOffsetDefinitionMode = () => {
    if (labwareOffsetDefinition) {
      dispatch(
        removeLabwareOffsetDefinition({
          labwareDefinitionURI,
        })
      );
    } else {
      dispatch(
        updateLabwareOffsetDefinitions({
          labwareDefinitionURI,
        })
      );
    }
    dispatch(setWorkflowIsSaved(false));
  };

  const handleWidthChange = (
    subField: 'gripped' | 'released',
    value: number | undefined
  ) => {
    const current = labwareOffsetDefinition ?? {};
    const currentWidths = current.gripping_widths ?? {};

    const newWidths = {
      ...currentWidths,
      [orientation]: {
        ...(currentWidths[orientation] ?? {}),
        [subField]: value,
      },
    };

    dispatch(
      updateLabwareOffsetDefinitions({
        labwareDefinitionURI,
        gripping_widths: newWidths,
      })
    );
    dispatch(setWorkflowIsSaved(false));
  };

  const handleOffsetChange = (
    field: 'gripping_depth_offset' | 'gripping_height_offset',
    value: number | undefined
  ) => {
    const current = labwareOffsetDefinition ?? {};
    const currentField =
      typeof current[field] === 'number'
        ? {
            portrait: current[field],
            landscape: current[field],
          }
        : current[field] ?? {};

    const newField = {
      ...currentField,
      [orientation]: value,
    };

    dispatch(
      updateLabwareOffsetDefinitions({
        labwareDefinitionURI,
        [field]: newField,
      })
    );
    dispatch(setWorkflowIsSaved(false));
  };

  return (
    <>
      <Typography
        variant="overline"
        color="text.primary"
        component="label"
        py={2}
      >
        Gripping Dimensions
      </Typography>

      <FormControlLabel
        sx={{ color: theme.palette.grey[700] }}
        control={
          <Checkbox
            id="simulate-drivers-checkbox"
            checked={!labwareOffsetDefinition}
            onChange={handleOffsetDefinitionMode}
          />
        }
        label="Use default SBS"
      />
      {!!labwareOffsetDefinition && (
        <>
          <UnitOfMeasurementSelect
            label="orientation"
            value={orientation}
            units={[
              DefinitionOrientation.LANDSCAPE,
              DefinitionOrientation.PORTRAIT,
            ]}
            onChange={(val) => setOrientation(val as DefinitionOrientation)}
          />
          <Stack direction="row" alignItems="flex-end" gap={1}>
            <NumberField
              decimal={true}
              label="width (gripped)"
              sx={sx}
              min={0}
              value={
                labwareOffsetDefinition?.gripping_widths?.[orientation]
                  ?.gripped ?? null
              }
              infoPopover={
                <InfoPopover
                  title="WIDTH (GRIPPED)"
                  copy="The total gripping width in mm."
                />
              }
              onChange={(e) => handleWidthChange('gripped', e ?? undefined)}
            />
            <NumberField
              decimal={true}
              label="width (released)"
              min={0}
              sx={sx}
              value={
                labwareOffsetDefinition?.gripping_widths?.[orientation]
                  ?.released ?? null
              }
              infoPopover={
                <InfoPopover
                  title="WIDTH (RELEASED)"
                  copy="The total released width in mm."
                />
              }
              onChange={(e) => handleWidthChange('released', e ?? undefined)}
            />
          </Stack>
          <Stack direction="row" alignItems="flex-end" gap={1}>
            <NumberField
              decimal={true}
              label="depth offset"
              sx={sx}
              value={
                labwareOffsetDefinition?.gripping_depth_offset?.[orientation] ??
                null
              }
              infoPopover={
                <InfoPopover
                  title="DEPTH OFFSET"
                  copy="The offset in depth from the standard gripping position in mm."
                />
              }
              onChange={(e) =>
                handleOffsetChange('gripping_depth_offset', e ?? undefined)
              }
            />
            <NumberField
              decimal={true}
              label="height offset"
              sx={sx}
              value={
                typeof labwareOffsetDefinition?.gripping_height_offset ===
                'number'
                  ? labwareOffsetDefinition?.gripping_height_offset
                  : labwareOffsetDefinition?.gripping_height_offset?.[
                      orientation
                    ] ?? null
              }
              infoPopover={
                <InfoPopover
                  title="HEIGHT OFFSET"
                  copy="The offset in height from the standard gripping position in mm."
                />
              }
              onChange={(e) =>
                handleOffsetChange('gripping_height_offset', e ?? undefined)
              }
            />
          </Stack>
        </>
      )}
    </>
  );
};
